import React, { useEffect, useState } from "react";
import T from "prop-types";
import { connect } from "react-redux";
import { Field } from "redux-form";
import { toastr } from "react-redux-toastr";
import { Col } from "react-bootstrap";
import { validator } from "LIB/validate-this";
import * as rules from "LIB/validation/rules";
import {
  CompanyName,
  DataTable,
  ExternalLink
} from "MODULES/shared/components";
import {
  form,
  Form as AppForm,
  Check,
  CheckGroup,
  FormInput,
  FormRow,
  TextArea
} from "MODULES/shared/components/Forms";
import {
  ATTACH_TAG_COPIES,
  ATTACH_TRACEABILITY
} from "MODULES/OutgoingQuotes/constants";
import api from "APP_ROOT/api";
import { properCase } from "LIB/formatters/text";
import SelectAttachmentTable from "MODULES/OutgoingQuotes/Detail/Send/SelectAttachmentTable";
import { withFieldAccessors } from "MODULES/shared/decorators/withFieldAccessors";
import {
  ATTACH_CERTIFICATE_OF_CONFORMANCE,
  ATTACH_PACKING_SLIP
} from "MODULES/SalesOrders/constants";
import { SUCCESS } from "LIB/submission";
import { AgnosticLoader } from "MODULES/shared/containers";
import _ from "lodash";
import { OTHER } from "MODULES/shared/constants";
import { API_BASE } from "APP_ROOT/constants";

const FORM_NAME = "sendQcDocumentsForm";

export function Form({
  close,
  getFieldValue,
  handleSubmit,
  lineItem,
  setFieldValue,
  modal
}) {
  const [attachments, setAttachments] = useState([]);
  const [inventoryAttachments, setInventoryAttachments] = useState([]);
  const [inventory, setInventory] = useState({
    purchase_order_line_items: [],
    sales_order_line_items: [],
    repair_order_line_items: []
  });
  const [obtainedFrom, setObtainedFrom] = useState({});
  const [obtainedFromType, setObtainedFromType] = useState({});

  const sendCoc = getFieldValue(ATTACH_CERTIFICATE_OF_CONFORMANCE);

  const onItemSelected = (item, type) => {
    setObtainedFrom(item);
    setObtainedFromType(type);
    setFieldValue("obtained_from_type")(type);
  };

  const radioSelect = (item, type) => (
    <input
      type="radio"
      name="obtained_from_id"
      className="radio-inline"
      checked={obtainedFrom.id === item.id}
      onChange={() => onItemSelected(item, type)}
    />
  );

  const renderCompanyLink = item => (
    <div>
      <CompanyName flattenedCompany={item} />
    </div>
  );

  const renderPORow = item => (
    <tr key={`po-${item.id}`}>
      <td />
      <td className="text-center">
        {radioSelect(item, "purchase_order_line_item")}
      </td>
      <td>{_.get(item, "purchase_order.code")}</td>
      <td>
        <CompanyName company={item.company} />
      </td>
    </tr>
  );

  const renderOtherRow = () => (
    <tr>
      <td />
      <td className="text-center">{radioSelect({ id: "other" }, "other")}</td>
      <td>Other</td>
      <td>
        <Field
          component={FormInput}
          disabled={obtainedFromType !== "other"}
          name="obtained_from_text"
          label="Obtained From Text"
          required={obtainedFromType === "other"}
        />
      </td>
    </tr>
  );

  const renderSORow = item => (
    <tr key={`so-${item.id}`}>
      <td />
      <td className="text-center">
        {radioSelect(item, "sales_order_line_item")}
      </td>
      <td>{item.sales_order_code}</td>
      <td>{renderCompanyLink(item)}</td>
    </tr>
  );

  const renderRORow = item => (
    <tr key={`ro-${item.id}`}>
      <td />
      <td className="text-center">
        {radioSelect(item, "repair_order_line_item")}
      </td>
      <td>{item.repair_order_code}</td>
      <td>{renderCompanyLink(item)}</td>
    </tr>
  );

  const fetch = id =>
    api.inventories.show(id, "all", "reference_record_details").then(resp => {
      setInventory(resp.data);

      return SUCCESS;
    });

  useEffect(() => fetch(lineItem.inventory_id), [lineItem.inventory_id]);

  useEffect(
    () => {
      Promise.all([
        api.attachments
          .list(lineItem.sales_order_id, "sales_orders")
          .then(({ data }) => {
            setAttachments(data.results);
          }),
        api.attachments
          .list(lineItem.inventory_id, "inventories")
          .then(({ data }) => {
            setInventoryAttachments(data.results);
          })
      ]);

      return function cleanup() {};
    },
    [lineItem]
  );
  const obtainedFromId =
    obtainedFrom.id !== OTHER && obtainedFrom.id
      ? `&obtained_from_id=${obtainedFrom.id}`
      : "";

  const boxFreeObtainedFromOtherText =
    obtainedFrom.id === OTHER
      ? `&obtained_from_text=${getFieldValue("obtained_from_text")}`
      : "";

  const url = `${API_BASE}/sales_order_line_items/${lineItem.id}/download_certificate_of_conformance?obtained_from_type=${obtainedFromType}${obtainedFromId}${boxFreeObtainedFromOtherText}`;
  const filesNotPresent = [
    inventory.traceability_attachment,
    inventory.tag_copies_attachment
  ].filter(f => !f).length;

  return (
    <AppForm handleSubmit={handleSubmit} modal={modal} close={close}>
      <AppForm.Body>
        <Field component={FormInput} name="to" label="To:" required />
        <Field component={FormInput} name="cc" label="CC:" />
        <Field component={FormInput} name="bcc" label="BCC:" />
        <Field component={FormInput} name="subject" label="Subject" required />
        <Field component={TextArea} name="body" label="Body" required />
        <div>
          <h4>Attachments</h4>
          <FormRow>
            <Col md={12}>
              <CheckGroup title="Attachments" justified>
                {inventory.traceability_attachment && (
                  <Field
                    name={`${ATTACH_TRACEABILITY}`}
                    component={Check}
                    label={properCase(ATTACH_TRACEABILITY)}
                  />
                )}
                {inventory.tag_copies_attachment && (
                  <Field
                    name={`${ATTACH_TAG_COPIES}`}
                    component={Check}
                    label={properCase(ATTACH_TAG_COPIES)}
                  />
                )}
                <Field
                  name={ATTACH_PACKING_SLIP}
                  component={Check}
                  label={properCase(ATTACH_PACKING_SLIP)}
                />
                <Field
                  name={ATTACH_CERTIFICATE_OF_CONFORMANCE}
                  component={Check}
                  label="Attach CoC"
                />
              </CheckGroup>
            </Col>
          </FormRow>
          <FormRow>
            {inventory.traceability_attachment && (
              <Col md={2} style={{ marginTop: 0 }}>
                <ExternalLink
                  url={inventory.traceability_attachment.public_download_url}
                >
                  Download Traceability
                </ExternalLink>
              </Col>
            )}
            {inventory.tag_copies_attachment && (
              <Col md={2} style={{ marginTop: 0 }}>
                <ExternalLink
                  url={inventory.tag_copies_attachment.public_download_url}
                >
                  Download Tag Copies
                </ExternalLink>
              </Col>
            )}
            <Col md={2} mdOffset={filesNotPresent * 2} style={{ marginTop: 0 }}>
              <ExternalLink
                url={`${API_BASE}/sales_orders/${lineItem.sales_order_id}/download_packing_slip`}
              >
                Download Packing Slip
              </ExternalLink>
            </Col>
            <Col
              md={2}
              mdOffset={filesNotPresent === 1 ? 2 : 0}
              style={{ marginTop: 0 }}
            >
              <ExternalLink url={url}>Download CoC</ExternalLink>
            </Col>
          </FormRow>
          {sendCoc && (
            <FormRow>
              <Col md={12}>
                <h4>Obtained From</h4>
                <AgnosticLoader fetch={fetch} id={lineItem.inventory_id}>
                  {({ loading }) => (
                    <DataTable loading={loading} loadingRows={1}>
                      <DataTable.Head>
                        <th />
                        <th />
                        <th>Code</th>
                        <th>Company Name</th>
                      </DataTable.Head>
                      <DataTable.Body>
                        {inventory.purchase_order_line_items
                          .map(renderPORow)
                          .concat(
                            inventory.sales_order_line_items
                              .filter(
                                s => s.exchange_sale && s.id !== lineItem.id
                              )
                              .map(renderSORow)
                          )
                          .concat(
                            inventory.repair_order_line_items.map(renderRORow)
                          )
                          .concat([
                            renderOtherRow({
                              obtainedFromType,
                              onFlatItemSelected: () =>
                                setObtainedFromType(OTHER),
                              onObtainedFromOther: {}
                            })
                          ])}
                      </DataTable.Body>
                    </DataTable>
                  )}
                </AgnosticLoader>
              </Col>
            </FormRow>
          )}
        </div>
        {inventoryAttachments &&
          inventoryAttachments.length > 0 && (
            <FormRow>
              <Col md={12}>
                <h4>Inventory Attachments</h4>
                <SelectAttachmentTable
                  attachments={inventoryAttachments}
                  getFieldValue={getFieldValue}
                  setFieldValue={setFieldValue}
                />
              </Col>
            </FormRow>
          )}
        {attachments &&
          attachments.length > 0 && (
            <FormRow>
              <Col md={12}>
                <h4>Sales Order Attachments</h4>
                <SelectAttachmentTable
                  attachments={attachments}
                  getFieldValue={getFieldValue}
                  setFieldValue={setFieldValue}
                />
              </Col>
            </FormRow>
          )}
      </AppForm.Body>
    </AppForm>
  );
}

Form.propTypes = {
  close: T.func.isRequired,
  getFieldValue: T.func.isRequired,
  handleSubmit: T.func.isRequired,
  setFieldValue: T.func.isRequired,
  salesOrder: T.shape({}),
  modal: T.bool
};

export default connect((state, props) => {
  const firstName = props.salesOrder.contact.name.split(" ")[0];
  let messageSubject = `${props.currentUser.tenant
    .name} QC Documents For PO# ${props.salesOrder
    .customer_purchase_order_number}`;

  let messageBody = `Hello ${firstName}, \n\nPlease see attached delivery of your QC documents.  Please do not reply to this email.\n\nIf you require any additional information, please contact your representative ${props
    .salesOrder.seller.email}\n\nBest regards,`;

  if (props.lineItem.customer_qc_preapproval_required) {
    messageSubject = `Trace Paperwork for Pre-Approval for PO# ${props
      .salesOrder.customer_purchase_order_number}`;

    messageBody = `Hello ${firstName}, \n\nPlease see the attached paperwork for pre-approval for the subject PO.\n\nPlease review and advise if acceptable prior to shipment.\n\nPlease note this order will not be shipped until we receive your written approval.\n\nBest regards,`;
  }

  const determineCC = () => {
    if (props.currentUser.email === props.salesOrder.seller.email) {
      if (props.salesOrder) {
        return (
          props.salesOrder.cc_email_address ||
          props.salesOrder.third_contact_email ||
          ""
        );
      }
      return "";
    }
    if (props.salesOrder && props.salesOrder.seller) {
      return props.salesOrder.seller.email || "";
    }
    return "";
  };

  return {
    attachments: [],
    initialValues: {
      attachment_ids: [],
      body: messageBody,
      to: [
        props.salesOrder.contact.email,
        props.salesOrder.cc_email_address,
        props.salesOrder.third_contact_email
      ]
        .filter(Boolean)
        .join(", "),
      cc: determineCC(),
      bcc: _.uniq(
        [
          props.currentUser.email,
          _.get(props.currentUser, "tenant.quality_control_bcc_emails")
        ].filter(f => f)
      ).join(","),
      subject: messageSubject
    }
  };
})(
  form({
    form: FORM_NAME,
    validate: values =>
      validator(values, f => {
        f.validate("body", "to").satisfies(rules.required);
        f.validate("to", "cc", "bcc").satisfies(rules.isValidEmail);
      }),
    onSubmit: async (data, _ev, props) => {
      await api.salesOrders.lineItems
        .sendQualityControlDocuments(props.lineItem.id, props.values)
        .then(() => {
          props.close();
          toastr.success("Success", "Documents successfully sent.");
        })
        .catch(() =>
          toastr.error(
            "Error",
            "Could not send documents. Please refresh page and try again."
          )
        );
    }
  })(withFieldAccessors(Form))
);



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/Detail/Body/LineItems/SendQualityControlDocuments/Form.jsx